import { lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, MapPin, Zap, PackageCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { scrollToId } from "@/components/Navbar";
import { MARQUEE_ITEMS } from "@/data/content";

const HeroScene = lazy(() => import("@/components/three/HeroScene"));

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const item = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } },
};

export const HeroSection = () => {
  return (
    <section
      id="home"
      className="relative min-h-screen flex flex-col justify-center hero-backdrop noise-overlay overflow-hidden pt-16"
      data-testid="hero-section"
    >
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-6 items-center py-14 lg:py-10">
        <motion.div variants={container} initial="hidden" animate="show" className="max-w-xl">
          <motion.div variants={item} className="flex flex-wrap items-center gap-2 mb-6">
            <Badge className="bg-secondary text-secondary-foreground border border-border rounded-full px-3 py-1 text-[11px] tracking-widest uppercase">
              <MapPin className="w-3 h-3 mr-1.5 text-primary" /> London, United Kingdom
            </Badge>
            <Badge className="bg-secondary text-secondary-foreground border border-border rounded-full px-3 py-1 text-[11px] tracking-widest uppercase">
              <ShieldCheck className="w-3 h-3 mr-1.5 text-primary" /> UK Registered · No. 13432441
            </Badge>
          </motion.div>

          <motion.h1
            variants={item}
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-foreground"
          >
            Engineering{" "}
            <span className="font-editorial italic text-primary">digital excellence</span>{" "}
            for businesses that demand more
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground"
          >
            Websites, mobile apps, e-commerce empires, Google-ranked marketing, secure
            infrastructure and custom business systems — built by an elite London consultancy
            that answers within 24 hours.
          </motion.p>

          <motion.div variants={item} className="mt-9 flex flex-wrap items-center gap-4">
            <Button
              size="lg"
              onClick={() => scrollToId("contact")}
              className="cta-shimmer bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl px-7 h-12 text-base shadow-[0_10px_40px_rgba(214,179,106,0.3)] hover:-translate-y-px transition-transform duration-200"
              data-testid="hero-primary-cta-button"
            >
              Start Your Project <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => scrollToId("services")}
              className="rounded-xl px-7 h-12 text-base border-border bg-secondary/40 text-foreground hover:bg-secondary/80 transition-colors duration-200"
              data-testid="hero-secondary-cta-button"
            >
              Explore Services
            </Button>
          </motion.div>

          <motion.div variants={item} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="flex items-center gap-2 text-xs text-muted-foreground font-mono-meta">
              <Zap className="w-3.5 h-3.5 text-primary" /> Live in 7 days maximum
            </span>
            <span className="flex items-center gap-2 text-xs text-muted-foreground font-mono-meta">
              <PackageCheck className="w-3.5 h-3.5 text-primary" /> Domain · Hosting · SSL · Database included
            </span>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative h-[340px] sm:h-[420px] lg:h-[560px] w-full"
          data-testid="hero-3d-canvas"
        >
          <Suspense fallback={<Skeleton className="w-full h-full rounded-3xl bg-secondary/40" />}>
            <HeroScene />
          </Suspense>
        </motion.div>
      </div>

      <div className="relative z-10 border-t border-border/60 bg-background/60 backdrop-blur-sm py-4 overflow-hidden" data-testid="hero-marquee">
        <div className="marquee-track">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((tech, i) => (
            <span key={i} className="flex items-center shrink-0">
              <span className="text-sm text-muted-foreground font-mono-meta px-6">{tech}</span>
              <span className="gold-dot opacity-50" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
